// ============================================
// ROUTER - React Router v6 Configuration
// ============================================

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthLayout } from './layouts/AuthLayout';
import { AppLayout } from './layouts/AppLayout';
import { RootLayout } from './layouts/RootLayout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { AlianzaRegisterPage } from './pages/auth/alianzas/AlianzaRegisterPage';
import { ALIANZAS } from './pages/auth/alianzas/alianzas.config';
import { RegisterSuccessPage } from './pages/auth/RegisterSuccessPage';
import { WelcomePage } from './pages/auth/WelcomePage';
import { RecoverPasswordPage } from './pages/auth/RecoverPasswordPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { VentasPage } from './pages/ventas/VentasPage';
import { ClientesPage } from './pages/clientes/ClientesPage';
import { InventarioPage } from './pages/inventarios/InventarioPage';
import { EditarProductoPage } from './pages/inventarios/EditarProductoPage';
import { IngresarProductoPage } from './pages/inventarios/IngresarProductoPage';
import { DetalleIngresoProductoPage } from './pages/inventarios/DetalleIngresoProductoPage';
import { CrearPackPage } from './pages/inventarios/CrearPackPage';
import { EditarPackPage } from './pages/inventarios/EditarPackPage';
import { EtiquetasGranelPage } from './pages/inventarios/EtiquetasGranelPage';
import { LoadingPage } from './pages/LoadingPage';
import { EncuestaPage } from './pages/encuestas/EncuestaPage';
import { SuscripcionPage } from './pages/suscripciones/activacion/SuscripcionPage';
import { SuscripcionEsperaPage } from './pages/suscripciones/activacion/SuscripcionEsperaPage';
import { SuscripcionPremiumPage } from './pages/suscripciones/premium/SuscripcionPremiumPage';
import { SuscripcionPremiumEsperaPage } from './pages/suscripciones/premium/SuscripcionPremiumEsperaPage';
import { RegistroTarjetaExitoPremiumPage } from './pages/suscripciones/premium/registro/RegistroTarjetaExitoPremiumPage';
import { RegistroTarjetaSuscripcionPremiumPage } from './pages/suscripciones/premium/registro/RegistroTarjetaSuscripcionPremiumPage';
import { RegistroTarjetaExitoPage } from './pages/suscripciones/activacion/registro/RegistroTarjetaExitoPage';
import { RegistroTarjetaSuscripcionPage } from './pages/suscripciones/activacion/registro/RegistroTarjetaSuscripcionPage';
import { CajaHomePage } from './pages/caja/CajaHomePage';
import { AperturaCajaPage } from './pages/caja/AperturaCajaPage';
import { RegistroCajaPage } from './pages/caja/RegistroCajaPage';
import { MiCajaPage } from './pages/caja/MiCajaPage';
import { CajaAdministracionPage } from './pages/caja/CajaAdministracionPage';
import { TutorialesPage } from './pages/tutoriales/TutorialesPage';
import { BoletasPage } from './pages/boletas/BoletasPage';
import { BoletaDetallePage } from './pages/boletas/BoletaDetallePage';
import { ConfiguracionPage } from './pages/configuracion/ConfiguracionPage';
import { EcommercePage } from './pages/ecommerce/EcommercePage';
import { FiadosPage } from './pages/fiados/FiadosPage';
import { DetalleFiadosClientePage } from './pages/fiados/DetalleFiadosClientePage';
import { ReportesPage } from './pages/reportes/ReportesPage';
import { DtePage } from './pages/dte/DtePage';
import { PrintPage } from './pages/print/PrintPage';
import { DevImpersonatePage } from './pages/dev/DevImpersonatePage';
import { TiendaPage } from './pages/tienda/TiendaPage';
import { DescargaEscritorioPage } from './pages/descarga/DescargaEscritorioPage';
import { ConsultaBoletaPage } from './pages/consulta-boleta/ConsultaBoletaPage';

const MiNegocioPage = lazy(() =>
  import('./pages/mi-negocio/MiNegocioPage').then((module) => ({
    default: module.MiNegocioPage,
  }))
);

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: '/auth',
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'register',
            element: <RegisterPage />,
          },
          {
            path: 'register-success',
            element: <RegisterSuccessPage />,
          },
          {
            path: 'welcome',
            element: <WelcomePage />,
          },
          {
            path: 'recover-password',
            element: <RecoverPasswordPage />,
          },
        ],
      },
      // Ruta de registro La Selecta (sin layout de auth)
      {
        path: '/selecta',
        element: <AlianzaRegisterPage partner={ALIANZAS.selecta} />,
      },
      // Ruta de registro Impulso Digital (sin layout de auth)
      {
        path: '/impulsodigital',
        element: <AlianzaRegisterPage partner={ALIANZAS.impulsodigital} />,
      },
      // Ruta de registro Poscta (sin layout de auth)
      {
        path: '/poscta',
        element: <AlianzaRegisterPage partner={ALIANZAS.poscta} />,
      },
      // Ruta de registro Tecnoline (sin layout de auth)
      {
        path: '/tecnoline',
        element: <AlianzaRegisterPage partner={ALIANZAS.tecnoline} />,
      },
      // Ruta de impersonación para uso interno del equipo
      {
        path: '/dev/impersonate',
        element: <DevImpersonatePage />,
      },
      // Página pública de descarga de la app de escritorio (sin layout, sin session gate)
      {
        path: '/descargar',
        element: <DescargaEscritorioPage />,
      },
      // Storefront público (sin autenticación)
      {
        path: '/tienda/:almacenId',
        element: <TiendaPage />,
      },
      // Consulta pública de boleta (sin autenticación)
      {
        path: '/consulta-boleta',
        element: <ConsultaBoletaPage />,
      },
      // Rutas para gates (sin layout, sin session gate)
      {
        path: '/loading',
        element: <LoadingPage />,
      },
      {
        path: '/print',
        element: <PrintPage />,
      },
      {
        path: '/encuestas/:encuestaId',
        element: (
          <ProtectedRoute skipSessionGate>
            <EncuestaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion',
        element: (
          <ProtectedRoute skipSessionGate>
            <SuscripcionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion/espera',
        element: (
          <ProtectedRoute skipSessionGate>
            <SuscripcionEsperaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion/registro/exito',
        element: (
          <ProtectedRoute skipSessionGate>
            <RegistroTarjetaExitoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion/registro/suscripcion',
        element: (
          <ProtectedRoute skipSessionGate>
            <RegistroTarjetaSuscripcionPage />
          </ProtectedRoute>
        ),
      },
      // Rutas de suscripción premium (sin layout, sin session gate)
      {
        path: '/suscripcion/premium/espera',
        element: (
          <ProtectedRoute skipSessionGate>
            <SuscripcionPremiumEsperaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion/premium/registro/exito',
        element: (
          <ProtectedRoute skipSessionGate>
            <RegistroTarjetaExitoPremiumPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/suscripcion/premium/registro/suscripcion',
        element: (
          <ProtectedRoute skipSessionGate>
            <RegistroTarjetaSuscripcionPremiumPage />
          </ProtectedRoute>
        ),
      },
      // Rutas protegidas de la aplicación
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        ),
        children: [
          // Rutas accesibles por todos los roles
          {
            path: 'ventas',
            element: <VentasPage />,
          },
          {
            path: 'tutoriales',
            element: <TutorialesPage />,
          },
          // Rutas exclusivas del propietario
          {
            path: 'dashboard',
            element: <DashboardPage />,
          },
          {
            path: 'mi-negocio',
            element: (
              <Suspense
                fallback={
                  <div className="flex min-h-[320px] items-center justify-center text-sm font-semibold text-gray-600">
                    Cargando Mi Negocio Pro...
                  </div>
                }
              >
                <MiNegocioPage />
              </Suspense>
            ),
          },
          {
            path: 'inventario',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <InventarioPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/ingresar',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <IngresarProductoPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/ingresar/detalle',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <DetalleIngresoProductoPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/crear-pack',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <CrearPackPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/editar-pack/:productoAlmacenId',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <EditarPackPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/editar/:productoAlmacenId',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <EditarProductoPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'inventario/etiquetas-granel',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <EtiquetasGranelPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'clientes',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <ClientesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'caja-360',
            element: (
              <ProtectedRoute>
                <CajaHomePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'caja/mi-caja',
            element: (
              <ProtectedRoute>
                <MiCajaPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'caja/apertura',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <AperturaCajaPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'caja/registro',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <RegistroCajaPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'caja/administracion',
            element: (
              <ProtectedRoute requiredRole="propietario" allowWithAnyPermission>
                <CajaAdministracionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'detalle-boleta',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <BoletasPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'detalle-boleta/:boletaId',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <BoletaDetallePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'configurar',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <ConfiguracionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'ecommerce',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <EcommercePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'fiados',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <FiadosPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'fiados/cliente/:clienteAlmacenId',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <DetalleFiadosClientePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'reporte',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <ReportesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'dte',
            element: (
              <ProtectedRoute requiredRole="propietario">
                <DtePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'suscripciones/premium',
            element: <SuscripcionPremiumPage />,
          },
        ],
      },
      {
        path: '*',
        element: <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold">404</h1>
            <p className="text-gray-600">Página no encontrada</p>
          </div>
        </div>,
      },
    ],
  },
]);
